
import { ScoreReason, LevelProfile } from "../contracts/scoring";
import { NormalizedDspResult, RawDspMetrics } from "../contracts/audio";
import { ScoreReport } from "./scoreTypes";
import { GLOBAL_SEVERITY_MATRIX, getTimingToleranceMs } from "./severityEngine";
import { APP_CONFIG } from "./config";
import { CalibrationStatus, isMetricActive, METRIC_GOVERNANCE_POLICY } from "./tabUnlockPolicy";

export interface ModuleResults {
    [key: string]: { score: number; weight: number } | undefined;
}

/**
 * GSC SCORING ENGINE V21.9.6 — HARDWARE TOLERANCE PATCH
 */
export function computeScore(
    rawDsp: NormalizedDspResult, 
    levelId: number, 
    harmonyMatchOverride?: number,
    userStatus: { isPremium: boolean, analysisCount: number } = { isPremium: true, analysisCount: 0 },
    calibrationStatus: CalibrationStatus = "VALID"
): ScoreReport {
  const m: RawDspMetrics = rawDsp.rawMetrics;
  const profile: LevelProfile = GLOBAL_SEVERITY_MATRIX[levelId] || GLOBAL_SEVERITY_MATRIX[4];
  const confidenceThreshold = APP_CONFIG.INTEGRITY_RULES.minConfidencePct / 100;

  if (m.rms_db < -85 || isNaN(m.rms_db) || !isFinite(m.rms_db)) {
    return createInvalidReport(m.rms_db, confidenceThreshold);
  }

  const bpm = isFinite(m.bpmDetected) ? m.bpmDetected : 90;
  const tol = getTimingToleranceMs(levelId, bpm);
  
  const safeScale = (v: any) => {
      const n = parseFloat(v);
      if (isNaN(n) || !isFinite(n) || n > 100000) return 0; 
      return Math.max(0, Math.min(100, Math.round(n)));
  };

  // --- NOUVEAU CALCUL DE TIMBRE (Indépendant du Rythme) ---
  // On compare les signatures spectrales (Basses, Médiums, Aigus)
  const stuB = rawDsp.spectral.stuBands;
  const refB = rawDsp.spectral.refBands;
  const spectralDiff = Math.abs(stuB.low - refB.low) + Math.abs(stuB.mid - refB.mid) + Math.abs(stuB.high - refB.high);
  
  // Courbe de tolérance matérielle : On est très indulgent sur les premiers 20% d'écart
  // car ils sont souvent dûs au hardware (micro laptop vs studio).
  const timbreMatch = Math.max(0, 1 - (spectralDiff / 1.5)); 
  const timbreScore = safeScale(Math.pow(timbreMatch, 0.5) * 100);

  // POSTURE VISION LAB : Courbe généreuse
  const rawStability = m.stability || 0.5;
  const postureScore = safeScale(Math.pow(rawStability, 0.4) * 100);

  // ATTAQUE : Courbe ultra-douce
  const rawTr = m.transient_ratio || 0.1;
  const attackScore = safeScale(Math.pow(rawTr, 0.35) * 100);

  const values: Record<string, number> = {
      rhythm: safeScale(calculateRhythmValue(m.timingDeviationMs, tol)),
      cleanliness: safeScale(100 - ((rawDsp.ghostNotes?.length || 0) * (profile.cleanliness.ghostPenaltyWeightPerGhost || 1))),
      tone: timbreScore, 
      nuance: safeScale(Math.min(1, (m.nuance?.relativeDynamics || 1.0) / 1.1) * 100),
      articulation: attackScore,
      synchronization: safeScale(rawStability * 100),
      harmony: safeScale((harmonyMatchOverride !== undefined && harmonyMatchOverride !== null) ? harmonyMatchOverride : Math.round(m.chromaClarity * 100)),
      posture: postureScore
  };

  let weightedSum = 0;
  let totalWeight = 0;
  const activeContributors: string[] = [];

  Object.keys(METRIC_GOVERNANCE_POLICY).forEach(id => {
      const rule = METRIC_GOVERNANCE_POLICY[id];
      if (id === 'audio') return; 

      const score = values[id] || 0;
      const weight = (profile.weights as any)[rule.weightKey] || 0;

      if (isMetricActive(id, levelId, calibrationStatus)) {
          weightedSum += (score * weight);
          totalWeight += weight;
          activeContributors.push(id);
      }
  });

  const globalScore = totalWeight > 0 ? safeScale(weightedSum / totalWeight) : 0;
  const qualityScore = safeScale(Math.round(rawDsp.confidence * 100));

  return {
    status: "VALID", 
    score: globalScore,
    reasons: [],
    metrics: {
      ...values,
      match_score: globalScore,
      quality_score: qualityScore,
      debug: { 
          ...m, 
          active_weight_contributors: activeContributors,
          calibration_status: calibrationStatus
      },
      timingErrorMs: Math.round((m.timingDeviationMs || []).reduce((a, b) => a + Math.abs(b), 0) / Math.max(1, m.timingDeviationMs?.length || 1)),
      toleranceMs: tol,
      bpm: bpm,
      confidence: rawDsp.confidence || 0,
      confidenceThreshold: confidenceThreshold,
      rhythm: values.rhythm, harmony: values.harmony, cleanliness: values.cleanliness, 
      articulation: values.articulation, nuance: values.nuance, tone: values.tone, 
      posture: values.posture, synchronization: values.synchronization,
      accentuation: null
    }
  };
}

function calculateRhythmValue(deviations: number[], tol: number): number {
    if (!deviations || deviations.length === 0) return 0;
    const avgDev = deviations.reduce((a, b) => a + Math.abs(b), 0) / deviations.length;
    if (avgDev <= tol) return 100;
    const normalizedError = (avgDev - tol) / (tol * 2);
    return Math.max(10, Math.round((1 - normalizedError) * 100));
}

function createInvalidReport(rms: number, confThreshold: number): ScoreReport {
    return {
      status: "INVALID", score: null, reasons: [],
      metrics: { 
          rhythm: 0, harmony: 0, tone: 0, cleanliness: 0, articulation: 0, nuance: 0, synchronization: 0,
          match_score: 0, quality_score: 0, posture: 0,
          confidence: 0, confidenceThreshold: confThreshold,
          accentuation: null, timingErrorMs: 0, toleranceMs: 0, bpm: 90
      }
    };
}

export function recomputeScoreWithNewLevel(payload: any, levelId: number, harmonyMatchOverride?: number, userStatus?: any): ScoreReport {
    const calibStatus = payload.calibration_status || (payload.debug?.calibration_status) || "VALID";
    const dspInput = payload.rawMetrics ? payload : { rawMetrics: payload, confidence: payload.confidence || 0.8, ...payload };
    return computeScore(dspInput as NormalizedDspResult, levelId, harmonyMatchOverride, userStatus, calibStatus);
}

export function aggregateAnalysisResults(results: ModuleResults, rms_db: number): { globalScore: number; status: "VALID" | "INVALID" } {
  if (rms_db < -75 || isNaN(rms_db) || !isFinite(rms_db)) return { globalScore: 0, status: "INVALID" };
  let weightedSum = 0; let totalWeight = 0;
  for (const key in results) {
    const res = results[key];
    if (res && typeof res.score === 'number' && typeof res.weight === 'number') {
        weightedSum += res.score * res.weight; totalWeight += res.weight;
    }
  }
  return { globalScore: totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 0, status: "VALID" };
}
